import { useState } from "react";
import { FormattedMessage } from "react-intl";
import classnames from "classnames";

import { Card } from "common/core/card";
import { Heading } from "common/core/typography";
import { DisclosureToggle, DisclosureContent } from "common/core/disclosure";
import { DescriptionList } from "common/core/description_list";
import {
  Attribute,
  identityAttributesFilter,
  identityAttributesSorter,
} from "common/details/identity/signer/attributes";
import { useId } from "util/html";

import Styles from "./index.module.scss";
import type {
  PersonalInformationCustomerSigner as CustomerSigner,
  PersonalInformationCustomerSigner_signerIdentities as SignerIdentity,
} from "./index.fragment.graphql";

function Attempt({
  signerIdentity,
  attempt,
  initiallyExpanded,
}: {
  signerIdentity: SignerIdentity;
  attempt: number;
  initiallyExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const attemptId = useId();

  return (
    <div className={classnames(Styles.attempt, expanded && Styles.expanded)}>
      <div>
        <FormattedMessage
          id="d77a960d-cbc8-4d3b-a9cd-372f825df995"
          defaultMessage="Attempt {attemptNumber}"
          values={{ attemptNumber: attempt }}
        >
          {(message) => <strong>{message}</strong>}
        </FormattedMessage>
        <DisclosureToggle
          onClick={() => setExpanded(!expanded)}
          expanded={expanded}
          controlsId={attemptId}
        />
      </div>
      <DisclosureContent expanded={expanded} id={attemptId}>
        <DescriptionList direction="horizontal" itemGap="extraLarge" className={Styles.information}>
          {[...(signerIdentity.identityAttributes || [])]
            .filter(identityAttributesFilter)
            .sort(identityAttributesSorter)
            .map((attribute) => (
              <Attribute key={attribute.id} attribute={attribute} />
            ))}
        </DescriptionList>
      </DisclosureContent>
    </div>
  );
}

export function PersonalInformation({ customerSigner }: { customerSigner: CustomerSigner }) {
  return (
    <Card fullWidth noMargin>
      <Heading level="h4" textStyle="allCapsLabelSmall">
        <FormattedMessage
          id="797f3b46-13c5-4e8c-8a1d-75c11d38f240"
          defaultMessage="Personal Details"
        />
      </Heading>
      {customerSigner.signerIdentities?.map((si, i, arr) => (
        <Attempt
          signerIdentity={si}
          key={si.id}
          attempt={arr.length - i}
          initiallyExpanded={i === 0}
        />
      ))}
    </Card>
  );
}
